import axios from "axios";
import { useFormik } from "formik";
import { useTranslation } from "next-i18next";
import Image from "next/image";
import { FC, Fragment, useState } from "react";

import Colors from "~/constants/colors";
import { isValidEmail } from "~/helpers/form.helpers";
import Trans from "~/types/translationKeys";
import Button from "../Button";
import Input from "../Input";
import Text from "../Text";

import styles from "./NewsletterWidget.module.scss";

const NewsletterWidget: FC = () => {
  const [completed, setCompleted] = useState(false);
  const { t } = useTranslation();

  const { touched, errors, getFieldProps, submitForm, isSubmitting } =
    useFormik({
      enableReinitialize: true,
      validateOnBlur: false,
      initialValues: { email: "" },
      validate: (values) => {
        const err: Record<string, string> = {};
        if (!values.email.trim()) {
          err.email = t(Trans.please_enter_your, {
            name: t(Trans.email).toLowerCase(),
          });
        } else if (!isValidEmail(values.email)) {
          err.email = t(Trans.wrong_email_format);
        }
        return err;
      },
      onSubmit: async (values) => {
        const formData = new FormData();
        formData.append("Email", values.email);
        formData.append("Sheet", "Newsletter");
        const res = await axios.post(
          `${process.env.NEXT_PUBLIC_SCRIPT_URL}`,
          formData
        );
        if (res.data.row) {
          setCompleted(true);
        }
      },
    });

  return (
    <div className={styles.newsletter_widget} data-aos="fade-down">
      <div className="flex flex-col w-full gap-y-2">
        <Image
          src="/images/newsletter_email.png"
          alt="email"
          width={32}
          height={32}
        />

        <h3 className={styles.title}>{t(Trans.join_our_newsletter)}</h3>

        <Text color={Colors.GREY_600} className="!leading-normal">
          {t(
            Trans.sign_up_for_our_weekly_newsletter_and_stay_ahead_of_the_curve_with_the_latest_ai_innovations_cx_genie_updates_and_much_more
          )}
        </Text>
      </div>

      <div className="flex flex-col w-full gap-y-2">
        {completed ? (
          <Text color={Colors.GREY_600}>
            {t(
              Trans.thank_you_for_joining_our_cx_genie_newsletter_please_check_your_email_for_sign_up_confirmation
            )}
          </Text>
        ) : (
          <Fragment>
            <Input
              className="!bg-transparent !border-[#C4C9D2]"
              containerClassName="w-full h-10"
              placeholder={t(Trans.your, {
                name: t(Trans.email_address),
              })}
              {...getFieldProps("email")}
              error={touched.email && errors.email}
            />
            <Button
              className={styles.button}
              loading={isSubmitting}
              onClick={submitForm}
            >
              {t(Trans.subscribe)}
            </Button>
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default NewsletterWidget;
